import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { UndoHistoryProvider, useUndoHistory } from '@/contexts/undo-history-context';

interface MainLayoutProps {
  children: React.ReactNode;
}

function timeAgo(timestamp: number): string {
  const s = Math.floor((Date.now() - timestamp) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function UndoHistoryButton() {
  const { actions, undo, pendingCount } = useUndoHistory();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on click-away
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const isEmpty = actions.length === 0;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className={`relative flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all
          ${open
            ? 'bg-gray-700 text-white'
            : pendingCount > 0
              ? 'text-amber-300 hover:bg-gray-700/60'
              : 'text-gray-400 hover:bg-gray-700/60 hover:text-gray-200'
          }`}
        title="Action history"
      >
        {/* Undo icon */}
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
        </svg>
        <span className="hidden sm:inline">History</span>
        {pendingCount > 0 && (
          <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-[10px] font-bold text-gray-900 leading-none">
            {pendingCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 z-50 rounded-xl border border-gray-700 bg-gray-900 shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
            <div>
              <p className="text-sm font-semibold text-white">Action History</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {isEmpty
                  ? 'No recent actions'
                  : pendingCount > 0
                    ? `${pendingCount} action${pendingCount !== 1 ? 's' : ''} can be undone`
                    : 'All actions have been undone'}
              </p>
            </div>
            {pendingCount === 0 && !isEmpty && (
              <span className="text-xs text-gray-600 italic">Session history</span>
            )}
          </div>

          {/* Actions list */}
          {isEmpty ? (
            <div className="px-4 py-8 flex flex-col items-center gap-2 text-center">
              <svg className="w-8 h-8 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z" />
              </svg>
              <p className="text-sm text-gray-500">Actions will appear here as you work</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-800 max-h-[360px] overflow-y-auto">
              {actions.map((action) => (
                <div
                  key={action.id}
                  className={`flex items-center gap-3 px-4 py-3 transition-colors
                    ${action.undone ? 'opacity-40' : 'hover:bg-gray-800/50'}`}
                >
                  {/* Status dot */}
                  <div className="flex-shrink-0 mt-0.5">
                    {action.undone ? (
                      <div className="w-6 h-6 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                        <svg className="w-3 h-3 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-amber-500/10 border border-amber-500/25 flex items-center justify-center">
                        <svg className="w-3 h-3 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-mono font-semibold truncate ${action.undone ? 'text-gray-500 line-through' : 'text-white'}`}>
                      {action.label}
                    </p>
                    {action.sublabel && (
                      <p className="text-xs text-gray-500 mt-0.5 truncate">{action.sublabel}</p>
                    )}
                    <p className="text-xs text-gray-600 mt-0.5">{timeAgo(action.timestamp)}</p>
                  </div>

                  {/* Action */}
                  {!action.undone && (
                    <button
                      onClick={() => undo(action.id)}
                      disabled={action.undoing}
                      className="flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold
                                 text-amber-300 bg-amber-500/10 hover:bg-amber-500/20
                                 border border-amber-500/20 hover:border-amber-500/35
                                 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                    >
                      {action.undoing ? (
                        <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                        </svg>
                      ) : (
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                        </svg>
                      )}
                      {action.undoing ? 'Undoing…' : 'Undo'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function MainLayout({ children }: MainLayoutProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <UndoHistoryProvider>
      <div className="min-h-screen bg-gray-900">
        <nav className="bg-gray-800 border-b border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16">
              <div className="flex items-center space-x-6">
                <div className="flex items-center gap-2.5">
                  <img src="/icon.svg" alt="CBT" className="w-8 h-8 rounded-md" />
                  <span className="text-base font-semibold text-white leading-tight">
                    Cincinnati Bulk Terminals
                  </span>
                </div>
                <nav className="flex items-center space-x-4">
                  <Link to="/dashboard" className="text-gray-300 hover:text-white transition-colors">Dashboard</Link>
                  <Link to="/manifests" className="text-gray-300 hover:text-white transition-colors">Manifests</Link>
                  <Link to="/inventory" className="text-gray-300 hover:text-white transition-colors">Inventory</Link>
                  <Link to="/orders" className="text-gray-300 hover:text-white transition-colors">Orders</Link>
                  <Link to="/locations" className="text-gray-300 hover:text-white transition-colors">Locations</Link>
                  {user?.role === 'Admin' && (
                    <Link to="/users" className="text-gray-300 hover:text-white transition-colors">Users</Link>
                  )}
                  {user?.role === 'Admin' && (
                    <Link to="/audit" className="text-gray-300 hover:text-white transition-colors">Audit Log</Link>
                  )}
                </nav>
              </div>

              <div className="flex items-center gap-3">
                <UndoHistoryButton />
                <span className="text-gray-400 text-sm">{user?.firstName} {user?.lastName}</span>
                <button
                  onClick={handleLogout}
                  className="px-4 py-2 text-sm bg-gray-700 hover:bg-gray-600 rounded transition-colors"
                >
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </nav>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {children}
        </main>
      </div>
    </UndoHistoryProvider>
  );
}
